import React, { useEffect, useMemo, useState } from 'react'
import { gql, useQuery, useMutation } from '@apollo/client'
import {
  Box,
  Chip,
  Container,
  Paper,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Tooltip,
} from '@mui/material'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import SendIcon from '@mui/icons-material/Send'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'

const RULE_FIELDS = `
  _id
  alertKey
  name
  description
  category
  enabled
  recipients
  includeSuperAdmins
  thresholdMinutes
  hasThreshold
  lastSentAt
  lastError
  lastRecipientCount
`

const GET_RULES = gql`query OrderAlertRules { orderAlertRules { ${RULE_FIELDS} } }`

const SAVE_RULE = gql`
  mutation SaveOrderAlertRule($input: OrderAlertRuleInput!) {
    saveOrderAlertRule(input: $input) { ${RULE_FIELDS} }
  }
`

const SEND_TEST = gql`
  mutation SendTestOrderAlert($alertKey: String!) {
    sendTestOrderAlert(alertKey: $alertKey) { sent recipientCount reason }
  }
`

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Last-send status for one rule.
 *
 * Deliberately loud about "enabled but going nowhere". The Courier stall alerts
 * were dead for months because a blank env var made them fail silently while
 * looking configured — a rule that reaches nobody has to be visibly broken here.
 */
function StatusLine({ rule }) {
  if (rule.enabled && rule.recipients.length === 0 && !rule.includeSuperAdmins) {
    return (
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'error.main' }}>
        <ErrorOutlineIcon sx={{ fontSize: 16 }} />
        <Typography variant="caption">On, but no recipients — this alert reaches nobody</Typography>
      </Stack>
    )
  }
  if (rule.lastError) {
    return (
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'error.main' }}>
        <ErrorOutlineIcon sx={{ fontSize: 16 }} />
        <Tooltip title={rule.lastError}>
          <Typography variant="caption" sx={{ maxWidth: 460, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Last attempt failed: {rule.lastError}
          </Typography>
        </Tooltip>
      </Stack>
    )
  }
  if (rule.lastSentAt) {
    return (
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'success.main' }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
        <Typography variant="caption">
          Last sent {new Date(rule.lastSentAt).toLocaleString()} to {rule.lastRecipientCount} recipient
          {rule.lastRecipientCount === 1 ? '' : 's'}
        </Typography>
      </Stack>
    )
  }
  return <Typography variant="caption" color="text.secondary">Never sent</Typography>
}

function RuleRow({ rule, onSave, onTest, busyKey }) {
  const [draft, setDraft] = useState('')
  const [recipients, setRecipients] = useState(rule.recipients)
  const [threshold, setThreshold] = useState(rule.thresholdMinutes ?? 0)
  const [enabled, setEnabled] = useState(rule.enabled)
  const [includeSuperAdmins, setIncludeSuperAdmins] = useState(rule.includeSuperAdmins)
  const [localError, setLocalError] = useState(null)

  // Re-sync when the server sends a newer version of this rule (after a save or
  // a test, which both update lastSentAt/lastError).
  useEffect(() => {
    setRecipients(rule.recipients)
    setThreshold(rule.thresholdMinutes ?? 0)
    setEnabled(rule.enabled)
    setIncludeSuperAdmins(rule.includeSuperAdmins)
  }, [rule])

  const dirty =
    enabled !== rule.enabled ||
    includeSuperAdmins !== rule.includeSuperAdmins ||
    Number(threshold) !== Number(rule.thresholdMinutes ?? 0) ||
    recipients.join(',') !== rule.recipients.join(',')

  const addDraft = () => {
    const value = draft.trim().toLowerCase()
    if (!value) return
    if (!EMAIL_RE.test(value)) { setLocalError(`"${value}" is not a valid email address`); return }
    if (recipients.includes(value)) { setDraft(''); return }
    setRecipients([...recipients, value])
    setDraft('')
    setLocalError(null)
  }

  const busy = busyKey === rule.alertKey

  return (
    <Box sx={{ py: 2.5 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ fontWeight: 600 }}>{rule.name}</Typography>
            {rule.hasThreshold && <Chip size="small" label="Exception" color="warning" variant="outlined" />}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {rule.description}
          </Typography>
          <Box sx={{ mt: 1 }}><StatusLine rule={rule} /></Box>
        </Box>

        <FormControlLabel
          control={<Switch checked={enabled} onChange={e => setEnabled(e.target.checked)} />}
          label={enabled ? 'On' : 'Off'}
          sx={{ mr: 0, minWidth: 92 }}
        />
      </Stack>

      <Stack spacing={1.5} sx={{ mt: 2 }}>
        <Box>
          <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
            {recipients.map(email => (
              <Chip
                key={email}
                label={email}
                size="small"
                onDelete={() => setRecipients(recipients.filter(r => r !== email))}
              />
            ))}
            {recipients.length === 0 && (
              <Typography variant="caption" color="text.secondary">No recipients yet</Typography>
            )}
          </Stack>
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              fullWidth
              placeholder="Add an email address and press Enter"
              value={draft}
              error={!!localError}
              helperText={localError}
              onChange={e => { setDraft(e.target.value); setLocalError(null) }}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addDraft() } }}
              onBlur={addDraft}
            />
            {rule.hasThreshold && (
              <TextField
                size="small"
                type="number"
                label="After (min)"
                inputProps={{ min: 0, max: 1440 }}
                sx={{ width: 150 }}
                value={threshold}
                onChange={e => setThreshold(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
              />
            )}
          </Stack>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={includeSuperAdmins}
                onChange={e => setIncludeSuperAdmins(e.target.checked)}
              />
            }
            label={<Typography variant="body2">Also send to all super admins</Typography>}
          />
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              startIcon={<SendIcon />}
              disabled={busy}
              onClick={() => onTest(rule.alertKey)}>
              Send test
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={!dirty || busy}
              onClick={() =>
                onSave({
                  alertKey: rule.alertKey,
                  enabled,
                  recipients,
                  includeSuperAdmins,
                  ...(rule.hasThreshold ? { thresholdMinutes: Number(threshold) || 0 } : {}),
                })
              }>
              {busy ? 'Saving…' : 'Save'}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  )
}

export default function OrderAlerts() {
  const { data, loading, error, refetch } = useQuery(GET_RULES, { fetchPolicy: 'cache-and-network' })
  const [message, setMessage] = useState(null)
  const [busyKey, setBusyKey] = useState(null)

  const [save] = useMutation(SAVE_RULE, {
    onCompleted: () => { setMessage({ type: 'success', text: 'Alert saved.' }); setBusyKey(null) },
    onError: e => { setMessage({ type: 'error', text: e.message }); setBusyKey(null) },
  })

  const [sendTest] = useMutation(SEND_TEST, {
    onCompleted: res => {
      const r = res.sendTestOrderAlert
      setMessage(
        r.sent
          ? { type: 'success', text: `Test sent to ${r.recipientCount} recipient${r.recipientCount === 1 ? '' : 's'}.` }
          : { type: 'error', text: `Test not sent — ${r.reason || 'no recipients configured'}` }
      )
      setBusyKey(null)
      refetch()
    },
    onError: e => { setMessage({ type: 'error', text: e.message }); setBusyKey(null) },
  })

  const rules = data?.orderAlertRules || []
  const lifecycle = useMemo(() => rules.filter(r => r.category === 'LIFECYCLE'), [rules])
  const exceptions = useMemo(() => rules.filter(r => r.category === 'EXCEPTION'), [rules])

  const onSave = input => { setBusyKey(input.alertKey); setMessage(null); save({ variables: { input } }) }
  const onTest = alertKey => { setBusyKey(alertKey); setMessage(null); sendTest({ variables: { alertKey } }) }

  const renderSection = (title, blurb, list) => (
    <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{blurb}</Typography>
      {list.map((rule, i) => (
        <React.Fragment key={rule.alertKey}>
          {i > 0 && <Divider />}
          <RuleRow rule={rule} onSave={onSave} onTest={onTest} busyKey={busyKey} />
        </React.Fragment>
      ))}
    </Paper>
  )

  return (
    <>
      <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #1e40af 100%)', px: { xs: 2, md: 4 }, pt: 3.5, pb: 5 }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ width: 52, height: 52, borderRadius: '14px', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <NotificationsActiveIcon sx={{ color: '#fff' }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>Order Alerts</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.72)' }}>
              Who gets told what, and how long an order may sit before it counts as a problem.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ mt: -3, mb: 5 }}>
        {loading && !data && <CircularProgress />}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}
        {message && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

        {!error && rules.length > 0 && (
          <>
            {renderSection(
              'Problems',
              'Sent when an order stops moving. Each one has its own threshold — checked every 60 seconds, and a change takes effect on the next check.',
              exceptions
            )}
            {renderSection(
              'Order activity',
              'Sent as an order moves through its normal stages. These are higher volume, so they start switched off — turn on the ones you want once recipients are set.',
              lifecycle
            )}
          </>
        )}
      </Container>
    </>
  )
}
